import { useState, useCallback } from 'react';
import { useElectronAPI } from './useElectronAPI';
import { toasterApi } from '@terra/ui-design-system';
import { useProxyStore } from '../store/proxy-store';
import { formatUpstreamProxyForIpc } from '../electron-shared/utils/proxy.util';

/**
 * Simplified hook for proxy actions that interact with Electron API
 * Uses the Zustand store as the single source of truth for settings
 * Only handles actions and loading states
 */
export const useProxyActions = () => {
  const electronAPI = useElectronAPI();
  const settings = useProxyStore((state) => state.settings);
  const setProxyRunning = useProxyStore((state) => state.setProxyRunning);
  const [isLoading, setIsLoading] = useState(false);

  // Start proxy server
  const startProxy = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await electronAPI.startProxy({
        host: settings.host,
        port: settings.port,
        interceptEnabled: settings.interceptEnabled,
        enableHttpsInterception: settings.enableHttps,
        httpsPort: settings.httpsPort,
        autoGenerateCerts: settings.autoGenerateCerts,
        certificatePath: settings.certificatePath,
        keyPath: settings.keyPath,
        caCertPath: settings.caCertPath,
        upstreamProxy: formatUpstreamProxyForIpc({
          upstreamEnabled: settings.upstreamEnabled,
          upstreamHost: settings.upstreamHost,
          upstreamPort: settings.upstreamPort,
        }),
        upstreamAuth:
          settings.upstreamEnabled && settings.upstreamAuth?.username
            ? {
                username: settings.upstreamAuth.username,
                password: settings.upstreamAuth.password ?? '',
              }
            : undefined,
        validateUpstreamTls: settings.validateUpstreamTls,
      });

      if (result.success) {
        setProxyRunning(true);
        toasterApi.success({
          title: 'Proxy Started',
          description: `Proxy server is running on port ${result.data?.port || settings.port}`,
        });
      } else {
        toasterApi.error({
          title: 'Failed to Start Proxy',
          description: result.error || 'Unknown error occurred',
        });
      }
    } catch (error) {
      console.error('Error starting proxy:', error);
      toasterApi.error({
        title: 'Proxy Start Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  }, [electronAPI, settings, setProxyRunning]);

  // Stop proxy server
  const stopProxy = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await electronAPI.stopProxy();

      if (result.success) {
        setProxyRunning(false);
        toasterApi.success({
          title: 'Proxy Stopped',
          description: 'Proxy server has been stopped',
        });
      } else {
        toasterApi.error({
          title: 'Failed to Stop Proxy',
          description: result.error || 'Unknown error occurred',
        });
      }
    } catch (error) {
      console.error('Error stopping proxy:', error);
      toasterApi.error({
        title: 'Proxy Stop Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  }, [electronAPI, setProxyRunning]);

  // Toggle intercept mode
  const toggleIntercept = useCallback(
    async (enabled: boolean) => {
      setIsLoading(true);

      try {
        const result = await electronAPI.toggleIntercept(enabled);

        if (result.success) {
          toasterApi.success({
            title: enabled ? 'Intercept Enabled' : 'Intercept Disabled',
            description: enabled
              ? 'Requests will now be intercepted for review'
              : 'Request interception has been disabled',
          });
        } else {
          toasterApi.error({
            title: 'Failed to Toggle Intercept',
            description: result.error || 'Unknown error occurred',
          });
        }
      } catch (error) {
        console.error('Error toggling intercept:', error);
        toasterApi.error({
          title: 'Intercept Toggle Error',
          description: 'An unexpected error occurred',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [electronAPI],
  );

  // Install CA certificate
  const installCACertificate = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await electronAPI.installCACertificate();

      if (result.success) {
        if (result.data?.requiresManualInstall && result.data?.redirectUrl) {
          const redirectUrl = result.data.redirectUrl;

          // Successful redirect to web interface approach
          toasterApi.success({
            title: 'Certificate Available',
            description:
              result.message ||
              'Certificate available for download at localhost:8080',
          });

          // Open the web interface in the default browser
          if (typeof window !== 'undefined') {
            // Enhanced delay with verification that proxy server is ready
            const verifyAndOpen = async () => {
              let attempts = 0;
              const maxAttempts = 10; // 5 seconds total

              while (attempts < maxAttempts) {
                try {
                  // Try to fetch the certificate endpoint to verify it's accessible
                  const response = await fetch(redirectUrl, {
                    method: 'HEAD',
                    signal: AbortSignal.timeout(1000), // 1 second timeout
                  });

                  if (response.ok || response.status === 200) {
                    // Server is ready, open the URL
                    window.open(redirectUrl, '_blank');

                    return;
                  }
                } catch {
                  // Server not ready yet, continue waiting
                }

                attempts++;
                await new Promise((resolve) => setTimeout(resolve, 500)); // Wait 500ms between attempts
              }

              // Fallback: open URL anyway after timeout
              console.warn(
                'Could not verify server readiness, opening URL anyway',
              );
              window.open(redirectUrl, '_blank');
            };

            verifyAndOpen();
          }

          return {
            success: true,
            requiresManualInstall: true,
            redirectUrl: result.data?.redirectUrl,
            message: result.message,
          };
        } else {
          // Automatic installation succeeded
          const method = result.data?.method || 'automatic';

          toasterApi.success({
            title: 'Certificate Installed',
            description: `CA certificate has been installed via ${method === 'user-keychain' ? 'user keychain' : 'system keychain'}`,
          });

          return { success: true };
        }
      } else {
        // Fallback error case (shouldn't happen with new approach)
        toasterApi.error({
          title: 'Installation Failed',
          description: result.error || 'Failed to install certificate',
        });

        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error installing certificate:', error);
      toasterApi.error({
        title: 'Installation Error',
        description: 'An unexpected error occurred',
      });

      return { success: false, error: 'An unexpected error occurred' };
    } finally {
      setIsLoading(false);
    }
  }, [electronAPI]);

  // Export CA certificate
  const exportCACertificate = useCallback(
    async (format: 'pem' | 'der' = 'pem') => {
      try {
        const result = await electronAPI.exportCACertificate(format);

        if (result.success) {
          toasterApi.success({
            title: 'Certificate Exported',
            description: 'CA certificate has been exported successfully',
          });
        } else {
          toasterApi.error({
            title: 'Export Failed',
            description: result.error || 'Failed to export certificate',
          });
        }
      } catch (error) {
        console.error('Error exporting certificate:', error);
        toasterApi.error({
          title: 'Export Error',
          description: 'An unexpected error occurred',
        });
      }
    },
    [electronAPI],
  );

  // Configure system proxy
  const configureSystemProxy = useCallback(
    async (enabled: boolean) => {
      setIsLoading(true);

      try {
        if (enabled) {
          const config = {
            host: settings.host,
            port: settings.port,
            enabled: true,
          };

          const result = await electronAPI.configureSystemProxy(config);

          if (result.success) {
            toasterApi.success({
              title: 'System Proxy Configured',
              description: 'System proxy settings have been updated',
            });
          } else {
            toasterApi.error({
              title: 'Failed to Configure System Proxy',
              description: result.error || 'Unknown error occurred',
            });
          }
        } else {
          const result = await electronAPI.restoreSystemProxy();

          if (result.success) {
            toasterApi.success({
              title: 'System Proxy Restored',
              description: 'Original system proxy settings have been restored',
            });
          } else {
            toasterApi.error({
              title: 'Failed to Restore System Proxy',
              description: result.error || 'Unknown error occurred',
            });
          }
        }
      } catch (error) {
        console.error('Error configuring system proxy:', error);
        toasterApi.error({
          title: 'System Proxy Error',
          description: 'An unexpected error occurred',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [electronAPI, settings],
  );

  return {
    isLoading,
    startProxy,
    stopProxy,
    toggleIntercept,
    installCACertificate,
    exportCACertificate,
    configureSystemProxy,
  };
};
